import DOMPurify from "dompurify";

import {
    ErrorLogGroup,
    GroupedErrorLog,
    ServerErrorLog,
    ServerErrorLogItem,
} from "../../../../../../Common/CandyApiClient/Types/ErrorLog/ServerErrorLog";
import { IPathMapper, NavigationPathMapper } from "../../PathUtils/NavigationPathMapper";
import { isLengthGreaterThenOrEqual, isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { BindingPath } from "../../CoreTypes";
import { hasHiddenText } from "../../../../Controls/FormParts/EmbeddedLog/ErrorList/ErrorItemDescription/Utils/Utils";
import { AttachInfo } from "../../../../../../Common/CandyApiClient/Types/Attachments/Attacments";

import {
    ErrorLog,
    ErrorLogGroupInfo,
    ErrorLogItem,
    ErrorLogItems,
    IErrorLogTaskResult,
    InterdocumentCheck,
    InterdocumentIssue,
    InterdocumentIssueSeverity,
    InterdocumentCheckType,
    ItemErrorMessage,
    ItemErrorMessageType,
    NavLinkErrorMessage,
} from "./Models";

// ,     ищем запятую
// (?!   Negative look-ahead open
// [^(]* Любые номера или символы кроме '(', 0 и более раз
// /]    закрывашка ']'
// )     Close the lookahead.
const errorDescriptionRegexp = /(\[([^[]+)\]\(([^)]+)\)|\[?[^[]+)/g;

function processErrorSourcePart(sourcePart: string): string {
    return sourcePart.replace(/\|\|/g, "/");
}

// В путях, может прийти вот такое [справНал=4,6]
export const splitPathWithComma = /,(?![^\[]*\])/;

export function safeHTML(unsafe: string | Node): string {
    DOMPurify.addHook("beforeSanitizeAttributes", function (node) {
        if (node.tagName !== undefined) {
            const allowedActionNames = ["openSalesModal"];
            const allowedActions = allowedActionNames.map(function (action: string) {
                return action + "();";
            });
            const dataAttrs = node.getAttributeNames() ?? [];

            for (const attr of dataAttrs) {
                const attrText = node.getAttribute(attr);
                const hasAllowedAction = isNotNullOrUndefined(attrText) && allowedActions.includes(attrText);
                if (attr.startsWith("on") && !hasAllowedAction) {
                    return node.removeAttribute(attr);
                }
            }
        }
        return null;
    });

    DOMPurify.addHook("afterSanitizeAttributes", function (node) {
        const isUnsafeLink = node.hasAttribute("href") && node.hasAttribute("target");

        if (isUnsafeLink) {
            if (node.hasAttribute("target")) {
                node.setAttribute("rel", "noopener noreferrer");
            }
        }
    });

    return DOMPurify.sanitize(unsafe, {
        FORBID_TAGS: ["script", "iframe", "object", "embed", "portal", "base", "textarea", "form"],
        FORBID_ATTR: ["src", "data-js", "data-onclick", "data-custom"],
        ALLOWED_TAGS: ["b", "q", "i", "strong", "a", "link", "em", "p", "block", "sub", "sup", "small", "br", "button"],
        ALLOWED_ATTR: ["href", "hintText", "buttonText", "buttonLink", "onclick", "rel", "target"],
    });
}

export function parseErrorDescription(
    description: string,
    pathMapper: IPathMapper,
    noLinks?: boolean,
    noPathMapping?: boolean
): ItemErrorMessage[] {
    const parsedDescription = [];

    while (true) {
        const match: RegExpExecArray | null = errorDescriptionRegexp.exec(description);
        if (match == undefined || !isLengthGreaterThenOrEqual(match, 4)) {
            break;
        }

        const text = match[1];
        const title = match[2];
        let url = match[3];

        if (!noLinks && (title || url)) {
            const urlBeginPosition = errorDescriptionRegexp.lastIndex - url.length - 2;
            url = parseUrl(description, urlBeginPosition) || url;
            errorDescriptionRegexp.lastIndex = urlBeginPosition + `(${url})`.length;

            const message: ItemErrorMessage = url.startsWith("http")
                ? {
                      type: ItemErrorMessageType.external,
                      text: title,
                      url: url,
                  }
                : {
                      type: ItemErrorMessageType.navLink,
                      text: title,
                      highlanderUrl: url,
                      bindingPaths:
                          (noPathMapping ?? false)
                              ? url.split(splitPathWithComma)
                              : url.split(splitPathWithComma).map(x => pathMapper.mapPath(x)),
                  };
            parsedDescription.push(message);
        } else {
            const message: ItemErrorMessage = {
                type: ItemErrorMessageType.text,
                text: text,
            };
            if (hasHiddenText(text)) {
                message.hideLinks = true;
            }
            parsedDescription.push(message);
        }
    }

    return parsedDescription;
}

function parseUrl(baseText: string, urlBeginPosition: number): string | null {
    let counter = 0;

    for (let i = urlBeginPosition; i < baseText.length; i++) {
        if (baseText[i] === "(") {
            counter++;
        }
        if (baseText[i] === ")") {
            counter--;
        }
        if (counter === 0) {
            return baseText.substring(urlBeginPosition + 1, i);
        }
    }

    return null;
}

function parseError(
    error: ServerErrorLogItem,
    pathMapper: NavigationPathMapper,
    noLinks?: boolean,
    noPathMapping?: boolean
): ErrorLogItem {
    return {
        id: error.id,
        highlanderPath: error.path,
        bindingPaths:
            (noPathMapping ?? false)
                ? error.path?.split(splitPathWithComma)
                : error.path?.split(splitPathWithComma)?.map(path => pathMapper.mapPath(path)),
        source: (error.source ?? "").split("/").map(processErrorSourcePart),
        tags: parseErrorTags(error.tags),
        highlanderUsedArguments: error.usedArguments,
        usedArgumentBindingPaths:
            (noPathMapping ?? false) ? error.usedArguments : error.usedArguments?.map(x => pathMapper.mapPath(x)),
        justification: error.justification ?? undefined,
        description: parseErrorDescription(error.description, pathMapper, noLinks, noPathMapping),
        touched: false,
    };
}

function parseErrorTags(tags: string[] | string | null): string[] {
    return tags ? (typeof tags === "string" ? [tags] : tags) : [];
}

// yarkeev: Можно было бы сделать тут всё по-умнее - такая информация есть на бекенде, и есть в ресурсах в элементах <attachmentform />
// Но решил пока не заморачиваться, и вот так вот захардкодить. Кажется ещё 10 лет не придётся менять :)
function getAttachmentGroupNameByAttachmentInfo(attachInfo: AttachInfo | undefined): string {
    const gfv = attachInfo?.meta.gfv;
    if (!gfv) {
        return "Приложение";
    }
    const gfToNameMap: { [gf: string]: string } = {
        ["1103"]: "Книга покупок (Раздел 8)",
        ["1105"]: "Дополнительные листы книги покупок (Раздел 8.1)",
        ["1102"]: "Книга продаж (Раздел 9)",
        ["1106"]: "Дополнительные листы книги продаж (Раздел 9.1)",
        ["1104"]: "Сведения из журнала выставленных счетов-фактур (Раздел 10)",
        ["1108"]: "Сведения из журнала полученных счетов-фактур (Раздел 11)",
        ["1107"]: "Сведения из счетов-фактур, выставленных лицами, указанными в п. 5 ст. 173 НК РФ (Раздел 12)",
    };
    const gf = gfv.substring(0, 4);

    const name = gfToNameMap[gf];
    if (!name) {
        return "Приложение";
    }

    return name;
}

export const DEFAULT_SUCCESS_DESCRIPTION = "Ошибок и предупреждений нет";

export function errorlogFromKewebToGroupedAdapter(
    getAttachInfo: (attachmentId: string) => AttachInfo | undefined,
    result?: IErrorLogTaskResult
): GroupedErrorLog {
    const mainLogErrors = (result?.mainReportCheckLog.errors ?? []).concat(
        result?.mainReportCrossCheckLog.errors ?? []
    );
    const mainLogWarnings = (result?.mainReportCheckLog.warnings ?? []).concat(
        result?.mainReportCrossCheckLog.warnings ?? []
    );
    const mainLogSuccesses = (result?.mainReportCheckLog.successes ?? []).concat(
        result?.mainReportCrossCheckLog.successes ?? []
    );

    const groups: ErrorLogGroup[] = [
        {
            groupName: "",
            errors: mainLogErrors,
            warnings: mainLogWarnings,
            successes: mainLogSuccesses,
        },
    ];

    if (result?.warrantCheckLog) {
        groups.push({
            groupName: "Результат проверки СоП",
            errors: result?.warrantCheckLog.errors ?? [],
            warnings: result?.warrantCheckLog.warnings ?? [],
            successes: result?.warrantCheckLog.successes ?? [],
            tid: "protocol-warrant",
        });
    }

    if (result?.digitalWarrantCheckLog) {
        groups.push({
            groupName: "Результат проверки Электронной доверенности",
            errors: result?.digitalWarrantCheckLog.errors ?? [],
            warnings: result?.digitalWarrantCheckLog.warnings ?? [],
            successes: result?.digitalWarrantCheckLog.successes ?? [],
            tid: "protocol-digital-warrant",
        });
    }

    const attachmentCheckLogs = result?.attachmentsCheckLogsWithIds || {};
    for (const attachmentId in attachmentCheckLogs) {
        const attachmentCheckLog = attachmentCheckLogs[attachmentId];
        groups.push({
            groupName: getAttachmentGroupNameByAttachmentInfo(getAttachInfo(attachmentId)),
            errors: attachmentCheckLog?.errors ?? [],
            warnings: attachmentCheckLog?.warnings ?? [],
            successes: attachmentCheckLog?.successes ?? [],
            groupAttachmentId: attachmentId,
            tid: "protocol-attachment",
        });
    }

    const groupedErrorLog: GroupedErrorLog = {
        propsName: result?.propsName,
        canSend: result?.canSend,
        errorLogName: result?.errorLogName,
        hasErrors: result?.hasErrors,
        marketplaceContext: result?.marketplaceContext,
        groups: groups,
        interdocumentCheckerResult: result?.interdocumentsCheckLog,
    };

    return groupedErrorLog;
}

export function serverErrorLogToGroupedAdapter(serverErrorLog: ServerErrorLog): GroupedErrorLog {
    return {
        propsName: serverErrorLog.propsName,
        errorLogName: serverErrorLog.errorLogName,
        groups: [
            {
                groupName: null,
                errors: serverErrorLog.errors,
                warnings: serverErrorLog.warnings,
                successes: serverErrorLog.successes ?? [],
            },
        ],
    };
}

export function parseErrorLog(
    errorLog: GroupedErrorLog,
    pathMapperGetter: (attachmentGroupId?: string) => NavigationPathMapper,
    noLinks?: boolean,
    noPathMapping?: boolean
): ErrorLog {
    const parsedErrors: ErrorLogItems = {};
    const parsedWarnings: ErrorLogItems = {};
    const parsedSuccesses: ErrorLogItems = {};
    errorLog.groups.forEach(group => {
        group.errors.forEach(
            error =>
                (parsedErrors[error.id] = parseError(
                    error,
                    pathMapperGetter(group.groupAttachmentId),
                    noLinks,
                    noPathMapping
                ))
        );
        group.warnings.forEach(
            error =>
                (parsedWarnings[error.id] = parseError(
                    error,
                    pathMapperGetter(group.groupAttachmentId),
                    noLinks,
                    noPathMapping
                ))
        );
        group.successes.forEach(
            error =>
                (parsedSuccesses[error.id] = parseError(
                    error,
                    pathMapperGetter(group.groupAttachmentId),
                    noLinks,
                    noPathMapping
                ))
        );
    });

    let interdocumentErrorsCount = 0;
    let interdocumentWarningsCount = 0;
    let interdocumentSuccessesCount = 0;

    if (errorLog.interdocumentCheckerResult?.checks) {
        interdocumentErrorsCount = getIssuesBySeverity(
            errorLog.interdocumentCheckerResult.checks,
            InterdocumentIssueSeverity.Error
        ).length;
        interdocumentWarningsCount = getIssuesBySeverity(
            errorLog.interdocumentCheckerResult.checks,
            InterdocumentIssueSeverity.Warning
        ).length;
        interdocumentSuccessesCount = errorLog.interdocumentCheckerResult.checks
            .filter(check => check.type === InterdocumentCheckType.Fine)
            .reduce((acc, check) => (acc += check.issues?.length ?? 0), 0);
    }

    return {
        propsName: errorLog.propsName,
        canSend: errorLog.canSend,
        hasErrors: errorLog.hasErrors,
        errorLogName: errorLog.errorLogName,
        errorsTotalCount: Object.keys(parsedErrors).length + interdocumentErrorsCount,
        warningsTotalCount: Object.keys(parsedWarnings).length + interdocumentWarningsCount,
        successesTotalCount: Object.keys(parsedSuccesses).length + interdocumentSuccessesCount,
        marketplaceContext: errorLog.marketplaceContext,
        errorGroupInfos: errorLog.groups.map<ErrorLogGroupInfo>(group => ({
            groupName: group.groupName,
            errorIds: group.errors.map(it => it.id),
            warningIds: group.warnings.map(it => it.id),
            successIds: group.successes.map(it => it.id),
            groupAttachmentId: group.groupAttachmentId,
            tid: group.tid,
        })),
        errors: parsedErrors,
        warnings: parsedWarnings,
        successes: parsedSuccesses,
        interdocumentCheckerResult: errorLog.interdocumentCheckerResult,
    };
}

function getIssuesBySeverity(checks: InterdocumentCheck[], severity: InterdocumentIssueSeverity): InterdocumentIssue[] {
    return checks
        .filter(
            check =>
                check.type === InterdocumentCheckType.Issues ||
                check.type === InterdocumentCheckType.Warning ||
                check.type === InterdocumentCheckType.Error
        )
        .map(check => check.discrepancyInfo?.issues ?? [])
        .flat()
        .filter(issue => issue.severity === severity);
}

export function isNavLinkErrorMessage(itemErrorMessage: ItemErrorMessage): itemErrorMessage is NavLinkErrorMessage {
    return itemErrorMessage.type === ItemErrorMessageType.navLink;
}

export function* getErrorItemBindingPaths(error: ErrorLogItem): Iterable<BindingPath> {
    yield* Iterator.from(error.description)
        .filter(isNavLinkErrorMessage)
        .flatMap(x => x.bindingPaths);
    if (error.bindingPaths != undefined) {
        yield* error.bindingPaths;
    }
    if (error.usedArgumentBindingPaths != undefined) {
        yield* error.usedArgumentBindingPaths;
    }
}

export const hasErrors = (errorLog: ErrorLog): boolean => errorLog.errorsTotalCount > 0;

export const checkCandyDisableSend = (errorLog: ErrorLog): boolean =>
    errorLog.isChecking ||
    errorLog.requestFailed ||
    hasErrors(errorLog) ||
    errorLog.marketplaceContext?.customScenarios?.bushTestDrive != undefined;

export const checkSendDisabled = (errorLog: ErrorLog) =>
    errorLog.canSend !== undefined ? !errorLog.canSend : checkCandyDisableSend(errorLog);
